// The gutter handle + its action menu.
//
// The handle is BOTH the drag source (via @platejs/dnd's handleRef, plus the
// data-block-id DndPlugin.onDragStart reads) AND the menu trigger. A drag is a
// pointer-down + move; a click (pointer-down + up with no drag) opens the menu.
// react-dnd owns the drag gesture, so we don't preventDefault on pointerdown
// (that would block the drag); instead we open the menu on `click`, which only
// fires when the gesture was a click, not a drag.
import { useRef } from 'react';
import { useEditorRef } from 'platejs/react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import clsx from 'clsx';
import { useDropdown } from '../../hooks/useDropdown';
import { duplicateBlock, insertParagraphBelow, removeBlock } from './blockHandleActions';
import styles from './BlockDraggable.module.scss';

interface Props {
    blockId: string;
    element: any;
    handleRef: (el: any) => void;
    anchorRef: React.RefObject<HTMLDivElement>;
}

export function BlockHandleMenu({ blockId, element, handleRef, anchorRef }: Props) {
    const editor = useEditorRef();
    const { open, setOpen, close, ref: menuRef } = useDropdown<HTMLDivElement>();
    const dragStartedRef = useRef(false);

    const runAction = (action: (ed: any, el: any) => void) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        action(editor, element);
        close();
    };

    const onMenuKeyDown = (action: (ed: any, el: any) => void) => (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action(editor, element);
            close();
        }
    };

    return (
        <div className={styles.handleWrap} ref={menuRef}>
            <button
                type="button"
                ref={handleRef}
                data-block-id={blockId}
                draggable
                className={clsx(styles.handle, open && styles.handleActive)}
                // Track whether a real drag started so the subsequent click
                // (which always fires after a drag too) does not also open the
                // menu. react-dnd fires dragstart only on an actual drag.
                onDragStart={() => {
                    dragStartedRef.current = true;
                }}
                onClick={(e) => {
                    e.preventDefault();
                    if (dragStartedRef.current) {
                        dragStartedRef.current = false;
                        return;
                    }
                    setOpen((o) => !o);
                }}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Block options · Drag to move"
                title="Drag to move · Click for options"
            >
                <DragIndicatorIcon style={{ fontSize: 16 }} />
            </button>
            {open && (
                <div className={styles.menu} role="menu" aria-label="Block actions" ref={anchorRef}>
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onMouseDown={runAction(insertParagraphBelow)}
                        onKeyDown={onMenuKeyDown(insertParagraphBelow)}
                    >
                        <AddIcon className={styles.menuIcon} fontSize="small" />
                        Insert below
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onMouseDown={runAction(duplicateBlock)}
                        onKeyDown={onMenuKeyDown(duplicateBlock)}
                    >
                        <ContentCopyIcon className={styles.menuIcon} fontSize="small" />
                        Duplicate
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className={clsx(styles.menuItem, styles.menuItemDanger)}
                        onMouseDown={runAction(removeBlock)}
                        onKeyDown={onMenuKeyDown(removeBlock)}
                    >
                        <DeleteOutlineIcon className={styles.menuIcon} fontSize="small" />
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
}
